import React from "react";
import { Field, TextField, LongText, RadioField, CheckboxGroupField, InlineCheck, InfoPanel, SectionTitle } from "./fields";
import { FileUpload } from "./FileUpload";
import { MiamBlock } from "./MiamBlock";
import { useSequentialReveal, ScrollLock } from "./reveal";
import { FUNDING_OPTIONS, MAX_UPLOAD_HINT, CASE_HELP_OPTIONS, MIAM_DETAIL_TRIGGERS } from "../../lib/options";
import { INTAKE } from "../../constants/testIds";
import { Button } from "../ui/button";
import { useBackGuard, hasAnswers } from "./useBackGuard";
import { RequiredDatesNote } from "./RequiredDatesNote";

const CONCERNS = [
  "Domestic abuse",
  "Harm or neglect involving a child",
  "Child abduction or removal",
  "Harassment or stalking",
  "Immediate risk to a person or child",
  "None of these",
  "Prefer not to say",
];
const ORDERS = [
  "Non-molestation order",
  "Occupation order",
  "Domestic Abuse Protection Notice or Order",
  "Restraining order",
  "Prohibited steps order",
  "Other — please state",
  "None",
  "I am not sure",
];
const HELP = CASE_HELP_OPTIONS;

export const RouteB = ({ form, set, config, onBack, onSubmit, submitting, carried = false, onSkip }) => {
  const d = form.routeB || {};
  const u = (k, v) => set("routeB", { ...d, [k]: v });
  const maxMb = config?.max_file_size_mb || 10;
  const { requestBack, dialog } = useBackGuard(!carried && hasAnswers(d), onBack);

  const appStatus = carried ? "Yes" : d.app_status;
  const orderSelected = (d.orders || []).some((o) => o !== "None" && o !== "I am not sure");

  const showScreening = appStatus === "No" || appStatus === "I am not sure";
  const showFull = appStatus === "Yes";

  const missingDates = [];
  if (orderSelected && !d.order_date && !d.order_date_unknown) missingDates.push("b-order-date");
  if (!carried && showFull && !d.app_date && !d.app_date_unknown) missingDates.push("b-app-date");
  if (!carried && d.has_deadline === "Yes" && !d.deadline_date && !d.deadline_date_unknown) missingDates.push("b-deadline-date");
  const datesOk = missingDates.length === 0;
  const otherOk =
    (!(d.help || []).includes("Other — please state") || !!(d.help_other && d.help_other.trim())) &&
    (!(d.orders || []).includes("Other — please state") || !!(d.order_type_other && d.order_type_other.trim()));
  const miamOk = !MIAM_DETAIL_TRIGGERS.includes(d.miam) || !!d.miam_signed;

  const flags = [
    !!appStatus,
    (d.concerns || []).length > 0,
    (d.who_raised || []).length > 0,
    (d.involvement || []).length > 0,
    (d.orders || []).length > 0,
    !!d.miam,
    ...(showFull ? [
      !!d.about,
      !!d.app_date || !!d.app_date_unknown,
      !!d.case_number || !!d.case_number_unknown,
      !!d.court_name || !!d.court_name_unknown,
      !!d.has_deadline,
      !!d.happened || !!d.happened_skip,
      (d.help || []).length > 0,
      !!d.outcome || !!d.outcome_unsure,
      !!d.accessibility || !!d.no_accessibility,
    ] : []),
  ];
  const visible = useSequentialReveal(flags, { enabled: !carried });

  return (
    <div className="space-y-8" data-testid="route-b-form">
      {dialog}
      <SectionTitle>{carried ? "Preparation request — remaining questions" : "Help with an existing or proposed case"}</SectionTitle>

      {carried && (
        <InfoPanel tone="gold" testid="b-carried-banner">
          We have carried forward the details you gave for your hearing (court, case number, key dates, what
          has happened, the outcome you are seeking and your budget). Please answer the few remaining
          questions below — you will not need to re-enter anything you have already provided.
        </InfoPanel>
      )}

      {/* B1 */}
      {!carried && (
      <Field num={1} label="Has an application already been made to the court?">
        <RadioField options={["Yes", "No", "I am not sure"]} value={appStatus} onChange={(v) => u("app_status", v)} name="b-app-status" testid="b-app-status" />
        {appStatus === "I am not sure" && (
          <InfoPanel tone="gold" testid="b-app-status-help">
            An application has usually been made if you or another party has sent an application to the
            court, received a case number, received court papers or been given a hearing date.
          </InfoPanel>
        )}
      </Field>
      )}

      {/* B2 Pre-application screening */}
      {(showScreening || showFull) && (
        <div className="space-y-5">
          <h4 className="font-serif text-lg text-brand-gold">Screening</h4>
          <Field num={2} show={visible > 1} label="Are there concerns about any of the following?" hint="Select all that apply.">
            <CheckboxGroupField options={CONCERNS} value={d.concerns} onChange={(v) => u("concerns", v)} exclusive={["None of these", "Prefer not to say"]} testid="b-concerns" />
            {(d.concerns || []).includes("Immediate risk to a person or child") && (
              <InfoPanel tone="danger" testid="b-immediate-risk">
                You have indicated an immediate risk. If anyone is in immediate danger, call 999 now. Your
                enquiry will be flagged as urgent. You can use Quick exit at any time.
              </InfoPanel>
            )}
          </Field>
          <Field num={3} show={visible > 2} label="Who has raised the concern?" hint="Select all that apply.">
            <CheckboxGroupField options={["I have", "The other party has", "Both parties", "A police officer, local authority or another professional", "Prefer not to say"]} value={d.who_raised} onChange={(v) => {
              const prev = d.who_raised || [];
              const IND = ["I have", "The other party has"];
              let next = v || [];
              const added = next.filter((x) => !prev.includes(x));
              if (added.includes("Both parties")) {
                next = next.filter((x) => !IND.includes(x));
              } else if (added.some((x) => IND.includes(x)) && next.includes("Both parties")) {
                next = next.filter((x) => x !== "Both parties");
              }
              if (next.includes("I have") && next.includes("The other party has")) {
                next = [...next.filter((x) => !IND.includes(x)), "Both parties"];
              }
              u("who_raised", next);
            }} exclusive={["Prefer not to say"]} testid="b-who-raised" />
          </Field>
          <Field num={4} show={visible > 3} label="Has there been involvement by any of the following?" hint="Select all that apply.">
            <CheckboxGroupField options={["Police", "Court", "Local authority or social services", "Domestic abuse support service", "None", "Prefer not to say"]} value={d.involvement} onChange={(v) => u("involvement", v)} exclusive={["None", "Prefer not to say"]} testid="b-involvement" />
          </Field>
          <Field num={5} show={visible > 4} label="Have any protective orders been applied for or made?" hint="Select all that apply.">
            <CheckboxGroupField options={ORDERS} value={d.orders} onChange={(v) => u("orders", v)} exclusive={["None", "I am not sure"]} testid="b-orders" extras={{
              "Other — please state": (d.orders || []).includes("Other — please state") ? (
                <div>
                  <TextField value={d.order_type_other} onChange={(v) => u("order_type_other", v)} placeholder="Please state *" testid="b-order-type-other" />
                  {!(d.order_type_other && d.order_type_other.trim()) && <p className="text-xs text-brand-danger mt-1">Please complete this field.</p>}
                </div>
              ) : null,
            }} />
            {orderSelected && (
              <div className="space-y-4 border-l-2 border-brand-gold pl-4 mt-3"><ScrollLock />
                <Field label="Was it applied for, made, refused or discharged?">
                  <RadioField options={["Applied for", "Made", "Refused", "Discharged"]} value={d.order_status} onChange={(v) => u("order_status", v)} name="b-order-status" testid="b-order-status" />
                </Field>
                <Field label="Which court dealt with it?"><TextField value={d.order_court} onChange={(v) => u("order_court", v)} testid="b-order-court" /></Field>
                <Field label="What was the date?" required={!d.order_date_unknown}>
                  <TextField type="date" value={d.order_date} onChange={(v) => u("order_date", v)} disabled={d.order_date_unknown} testid="b-order-date" />
                  <InlineCheck checked={d.order_date_unknown} onChange={(v) => u("order_date_unknown", !!v)} label="I do not know" testid="b-order-date-unknown" />
                </Field>
                <Field label="Is the order currently in force?"><RadioField options={["Yes", "No", "I do not know"]} value={d.order_in_force} onChange={(v) => u("order_in_force", v)} name="b-order-force" testid="b-order-force" /></Field>
                <Field label="Upload the order, if available" optional hint={MAX_UPLOAD_HINT(maxMb)}><FileUpload value={d.order_files} onChange={(v) => u("order_files", v)} maxMb={maxMb} testid="b-order-upload" /></Field>
              </div>
            )}
          </Field>
          <Field num={6} show={visible > 5} label="Have you attended or arranged a Mediation Information and Assessment Meeting (MIAM)?">
            <MiamBlock d={d} u={u} config={config} prefix="b-miam" />
          </Field>
        </div>
      )}

      {/* B3 Full form */}
      {showFull && (
        <div className="space-y-5">
          <h4 className="font-serif text-lg text-brand-gold">{carried ? "Preparation details" : "About the case"}</h4>
          {!carried && (<>
          <Field num={7} show={visible > 6} label="What is the case about?"><LongText value={d.about} onChange={(v) => u("about", v)} testid="b-about" /></Field>
          <Field num={8} show={visible > 7} label="When was the application made?" required={!d.app_date_unknown}>
            <TextField type="date" value={d.app_date} onChange={(v) => u("app_date", v)} disabled={d.app_date_unknown} testid="b-app-date" />
            <InlineCheck checked={d.app_date_unknown} onChange={(v) => u("app_date_unknown", !!v)} label="I do not know" testid="b-app-date-unknown" />
          </Field>
          <Field num={9} show={visible > 8} label="What is the case number?">
            <TextField value={d.case_number} onChange={(v) => u("case_number", v)} disabled={d.case_number_unknown} testid="b-case-number" />
            <InlineCheck checked={d.case_number_unknown} onChange={(v) => u("case_number_unknown", !!v)} label="I do not know" testid="b-case-number-unknown" />
          </Field>
          <Field num={10} show={visible > 9} label="Which court is dealing with the case?">
            <TextField value={d.court_name} onChange={(v) => u("court_name", v)} disabled={d.court_name_unknown} placeholder="Court name" testid="b-court-name" />
            <InlineCheck checked={d.court_name_unknown} onChange={(v) => u("court_name_unknown", !!v)} label="I do not know" testid="b-court-name-unknown" />
          </Field>
          <Field num={11} show={visible > 10} label="Is there a hearing or court deadline?">
            <RadioField options={["Yes", "No", "I do not know"]} value={d.has_deadline} onChange={(v) => u("has_deadline", v)} name="b-has-deadline" testid="b-has-deadline" />
            {d.has_deadline === "Yes" && (
              <div className="space-y-4 border-l-2 border-brand-gold pl-4 mt-3"><ScrollLock />
                <Field label="Hearing or deadline type"><TextField value={d.deadline_type} onChange={(v) => u("deadline_type", v)} testid="b-deadline-type" /></Field>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Field label="Date" required={!d.deadline_date_unknown}>
                    <TextField type="date" value={d.deadline_date} onChange={(v) => u("deadline_date", v)} disabled={d.deadline_date_unknown} testid="b-deadline-date" />
                    <InlineCheck checked={d.deadline_date_unknown} onChange={(v) => u("deadline_date_unknown", !!v)} label="I do not know" testid="b-deadline-date-unknown" />
                  </Field>
                  <Field label="Time"><TextField type="time" value={d.deadline_time} onChange={(v) => u("deadline_time", v)} testid="b-deadline-time" /></Field>
                </div>
                <Field label="Action required"><LongText value={d.deadline_action} onChange={(v) => u("deadline_action", v)} testid="b-deadline-action" /></Field>
              </div>
            )}
          </Field>
          <Field num={12} show={visible > 11} label="What has happened so far?">
            <LongText value={d.happened} onChange={(v) => u("happened", v)} testid="b-happened" />
            {!(d.happened || d.happened_skip) && (
              <Button type="button" variant="outline" onClick={() => u("happened_skip", true)} data-testid="b-happened-continue" className="mt-3 rounded-sm border-brand-gold text-brand-black hover:bg-brand-gold/10">
                Continue / Skip this step
              </Button>
            )}
          </Field>
          </>)}
          <Field num={13} show={visible > 12} label="What help would you like?" hint="Select all that apply.">
            <CheckboxGroupField options={HELP} value={d.help} onChange={(v) => u("help", v)} testid="b-help" extras={{
              "Other — please state": (d.help || []).includes("Other — please state") ? (
                <div>
                  <TextField value={d.help_other} onChange={(v) => u("help_other", v)} placeholder="Please state what help you need *" testid="b-help-other" />
                  {!(d.help_other && d.help_other.trim()) && <p className="text-xs text-brand-danger mt-1">Please complete this field.</p>}
                </div>
              ) : null,
            }} />
          </Field>
          {!carried && (<>
          <Field num={14} show={visible > 13} label="What outcome are you seeking?">
            <LongText value={d.outcome} onChange={(v) => u("outcome", v)} disabled={d.outcome_unsure} testid="b-outcome" />
            <InlineCheck checked={d.outcome_unsure} onChange={(v) => u("outcome_unsure", !!v)} label="I am not sure" testid="b-outcome-unsure" />
          </Field>
          <Field num={15} show={visible > 14} label="Do you have any communication or accessibility requirements?">
            <LongText value={d.accessibility} onChange={(v) => u("accessibility", v)} disabled={d.no_accessibility} testid="b-accessibility" />
            <InlineCheck checked={d.no_accessibility} onChange={(v) => u("no_accessibility", !!v)} label="No additional requirements" testid="b-no-accessibility" />
          </Field>
          </>)}
        </div>
      )}

      {(carried || visible >= flags.length) && !datesOk && (
        <RequiredDatesNote missing={missingDates} />
      )}
      <div className="flex items-center justify-between pt-6 border-t border-border">
        <Button variant="ghost" onClick={carried ? onSkip : requestBack} data-testid={carried ? "b-skip-linked" : INTAKE.back} className="text-brand-darkgrey rounded-sm">
          {carried ? "Skip — I'll do this later" : "Back"}
        </Button>
        {(carried || visible >= flags.length) && (
          <Button onClick={onSubmit} disabled={submitting || !appStatus || !datesOk || !otherOk || !miamOk} data-testid={INTAKE.submit} className="bg-brand-black text-white hover:bg-brand-gold rounded-sm px-8">
            {submitting ? "Submitting…" : carried ? "Submit preparation request" : "Submit enquiry"}
          </Button>
        )}
      </div>
    </div>
  );
};
